import { useMutation } from '@tanstack/react-query';
import { apiClient } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import type { Network } from '../../../constants';

/** X accepts video and image only; the backend rejects html for X. */
export type ReuploadCreativeType = 'image' | 'video';

export interface ReuploadCreativeRef {
  /** Primary key of the source row (the digit part of `video:7`), not the ADAM creative_id field. */
  id: number;
  type: ReuploadCreativeType;
}

/** `{ X: [{ id: 7, type: 'image' }] }`, keyed by target network. Only `X` is supported. */
export type ReuploadCreativesParams = Partial<Record<Network, ReuploadCreativeRef[]>>;

interface ReuploadCreativePayload {
  id: number;
  type: ReuploadCreativeType;
}

interface ReuploadCreativesPayload {"creatives": Partial<Record<Network, ReuploadCreativePayload[]>>}

const toReuploadCreativesPayload = (data: ReuploadCreativesParams): ReuploadCreativesPayload => {
  const payload: ReuploadCreativesPayload = {"creatives": {}};

  (Object.keys(data) as Network[]).forEach((network) => {
    const refs = data[network];
    if (!refs) return;
    payload.creatives[network] = refs.map(({ id, type }) => ({ id, type }));
  });

  return payload;
};

export interface ReuploadCreativesResponse {
  job_id: string;
}

/** The job finishes with the same `job_status` result rows as Facebook create-ad. */
export const reuploadCreatives = async (data: ReuploadCreativesParams): Promise<ReuploadCreativesResponse> => {
  const res = await apiClient.post<ReuploadCreativesResponse>(
    '/api/v1/creative_manager/creative/reupload',
    toReuploadCreativesPayload(data),
  );
  return res.data;
};

export const useReuploadCreatives = ({ config }: { config?: MutationConfig<typeof reuploadCreatives> } = {}) =>
  useMutation({ ...config, mutationFn: reuploadCreatives });
